import React, { useMemo, useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Card,
} from '@wove/web-ui'
import { cn } from '@wove/web-ui'
import { useCustomFields } from './useCustomFields'
import {
  RFQ_FIELD_OPTIONS,
  SHIPMENT_FIELD_OPTIONS,
  CONDITION_ONLY_RFQ_FIELDS,
  CONDITION_ONLY_SHIPMENT_FIELDS,
  SHIPMENT_CONTAINER_FIELD_OPTIONS,
} from './fieldOptions'

interface ValidationCondition {
  fieldPath: string
  operator: 'equals' | 'contains' | 'in' | 'not_equals' | 'exists' | 'not_exists' | 'matches_regex'
  value?: any
}

interface ValidationConditionGroup {
  operator: 'AND' | 'OR'
  conditions: (ValidationCondition | ValidationConditionGroup)[]
}

interface ConditionBuilderProps {
  conditions: ValidationConditionGroup
  onChange: (conditions: ValidationConditionGroup) => void
}

const OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In List' },
  { value: 'exists', label: 'Exists' },
  { value: 'not_exists', label: 'Not Exists' },
  { value: 'matches_regex', label: 'Matches Regex' },
]

function ConditionGroupBuilder({
  group,
  onChange,
  onRemove,
  depth = 0,
}: {
  group: ValidationConditionGroup
  onChange: (group: ValidationConditionGroup) => void
  onRemove?: () => void
  depth?: number
}) {
  const addCondition = () => {
    onChange({
      ...group,
      conditions: [...group.conditions, { fieldPath: '', operator: 'equals', value: '' }],
    })
  }

  const addGroup = () => {
    onChange({
      ...group,
      conditions: [...group.conditions, { operator: 'AND', conditions: [] }],
    })
  }

  const updateCondition = (
    index: number,
    condition: ValidationCondition | ValidationConditionGroup,
  ) => {
    const newConditions = [...group.conditions]
    newConditions[index] = condition
    onChange({ ...group, conditions: newConditions })
  }

  const removeCondition = (index: number) => {
    const newConditions = group.conditions.filter((_, i) => i !== index)
    onChange({ ...group, conditions: newConditions })
  }

  const toggleOperator = () => {
    onChange({ ...group, operator: group.operator === 'AND' ? 'OR' : 'AND' })
  }

  return (
    <Card className={cn('p-4', depth > 0 && 'ml-8 mt-2')}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Badge
            variant={group.operator === 'AND' ? 'default' : 'secondary'}
            className="cursor-pointer"
            onClick={toggleOperator}
          >
            {group.operator}
          </Badge>
          <span className="text-sm text-gray-600">
            {group.operator === 'AND' ? 'All conditions must match' : 'Any condition must match'}
          </span>
        </div>
        {onRemove && (
          <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {group.conditions.map((condition, index) => {
          if ('conditions' in condition) {
            return (
              <ConditionGroupBuilder
                key={index}
                group={condition as ValidationConditionGroup}
                onChange={(updated) => updateCondition(index, updated)}
                onRemove={() => removeCondition(index)}
                depth={depth + 1}
              />
            )
          } else {
            return (
              <ConditionRow
                key={index}
                condition={condition as ValidationCondition}
                onChange={(updated) => updateCondition(index, updated)}
                onRemove={() => removeCondition(index)}
              />
            )
          }
        })}
      </div>

      <div className="flex gap-2 mt-4">
        <Button type="button" variant="outline" size="sm" onClick={addCondition}>
          <Plus className="h-4 w-4 mr-2" />
          Add Condition
        </Button>
        {depth < 2 && (
          <Button type="button" variant="outline" size="sm" onClick={addGroup}>
            <Plus className="h-4 w-4 mr-2" />
            Add Group
          </Button>
        )}
      </div>
    </Card>
  )
}

function ConditionRow({
  condition,
  onChange,
  onRemove,
}: {
  condition: ValidationCondition
  onChange: (condition: ValidationCondition) => void
  onRemove: () => void
}) {
  const { data: customFields = [] } = useCustomFields()
  const needsValue = !['exists', 'not_exists'].includes(condition.operator)
  const [tagInput, setTagInput] = useState('')

  // Combine all fields including condition-only nested fields
  const allFields = useMemo(() => {
    const customFieldOptions = customFields.map((field) => ({
      value: `customFields.${field.field_key}`,
      label: field.field_name,
      category: field.applies_to === 'rfq' ? 'custom_rfq' : 'custom_shipment',
    }))

    return [
      ...RFQ_FIELD_OPTIONS,
      ...CONDITION_ONLY_RFQ_FIELDS,
      ...SHIPMENT_FIELD_OPTIONS,
      ...CONDITION_ONLY_SHIPMENT_FIELDS,
      ...SHIPMENT_CONTAINER_FIELD_OPTIONS,
      ...customFieldOptions,
    ]
  }, [customFields])

  return (
    <div className="flex items-center gap-2">
      <Select
        value={condition.fieldPath}
        onValueChange={(value) => onChange({ ...condition, fieldPath: value })}
      >
        <SelectTrigger className="w-48">
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_divider_rfq" disabled>
            <span className="font-semibold">RFQ Fields</span>
          </SelectItem>
          {RFQ_FIELD_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
          {CONDITION_ONLY_RFQ_FIELDS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
              <span className="text-xs text-gray-500 ml-2">(nested)</span>
            </SelectItem>
          ))}
          {customFields
            .filter((f) => f.applies_to === 'rfq')
            .map((field) => (
              <SelectItem key={field.field_key} value={`customFields.${field.field_key}`}>
                {field.field_name}
                <span className="text-xs text-gray-500 ml-2">(custom)</span>
              </SelectItem>
            ))}

          <SelectItem value="_divider_shipment" disabled>
            <span className="font-semibold">Shipment Fields</span>
          </SelectItem>
          {SHIPMENT_FIELD_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
          {CONDITION_ONLY_SHIPMENT_FIELDS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
              <span className="text-xs text-gray-500 ml-2">(nested)</span>
            </SelectItem>
          ))}
          {customFields
            .filter((f) => f.applies_to === 'shipment')
            .map((field) => (
              <SelectItem key={field.field_key} value={`customFields.${field.field_key}`}>
                {field.field_name}
                <span className="text-xs text-gray-500 ml-2">(custom)</span>
              </SelectItem>
            ))}

          <SelectItem value="_divider_container" disabled>
            <span className="font-semibold">Container Fields</span>
          </SelectItem>
          {SHIPMENT_CONTAINER_FIELD_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
          {customFields
            .filter((f) => f.applies_to === 'container')
            .map((field) => (
              <SelectItem key={field.field_key} value={`customFields.${field.field_key}`}>
                {field.field_name}
                <span className="text-xs text-gray-500 ml-2">(custom)</span>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      <Select
        value={condition.operator}
        onValueChange={(value) => onChange({ ...condition, operator: value as any })}
      >
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Select operator" />
        </SelectTrigger>
        <SelectContent>
          {OPERATOR_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {needsValue && (
        <>
          {condition.operator === 'in' ? (
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap gap-1 p-2 border rounded-md min-h-[38px]">
                {Array.isArray(condition.value) &&
                  condition.value.map((val, idx) => (
                    <Badge
                      key={idx}
                      variant="secondary"
                      className="px-2 py-1 flex items-center gap-1"
                    >
                      {val}
                      <button
                        type="button"
                        onClick={() => {
                          const newValues = condition.value.filter((_: any, i: number) => i !== idx)
                          onChange({ ...condition, value: newValues })
                        }}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault()
                      const trimmed = tagInput.trim()
                      if (trimmed) {
                        const currentValues = Array.isArray(condition.value) ? condition.value : []
                        onChange({ ...condition, value: [...currentValues, trimmed] })
                        setTagInput('')
                      }
                    }
                  }}
                  placeholder="Type and press Enter to add"
                  className="flex-1 border-0 shadow-none px-1 focus:ring-0 min-w-[150px]"
                />
              </div>
              <p className="text-xs text-gray-500">Press Enter or comma to add values</p>
            </div>
          ) : (
            <Input
              value={condition.value || ''}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              placeholder="Enter value"
              className="flex-1"
            />
          )}
        </>
      )}

      <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-8 w-8">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

export function ConditionBuilder({ conditions, onChange }: ConditionBuilderProps) {
  return (
    <div>
      <ConditionGroupBuilder group={conditions} onChange={onChange} />
    </div>
  )
}
